import React, { Component } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import cn from 'classnames';
import moment from 'moment';
import find from 'lodash/find';
import range from 'lodash/range';
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import upperFirst from 'lodash/upperFirst';

import { browserHistory } from '../../browserHistory';
import { getCalendarByOperatorId, getCalendarHeatmap } from '../../services/apiSchedule';
import {
  EditableSelect,
  ScheduleSettingsCalendar,
  ScheduleSettingsEditPopup,
  ScheduleSettingsOperatorList,
} from '../../components';

import {
  exposeComponentToGlobalScope,
  isMobileSizeScreen,
  objectDiffShallow,
  withMomentLocale,
} from '../../utils/utilsGlobal';

import styles from './ScheduleSettings.scss';

const mapStateToProps = state => {
  return {
    currentCompany: state.currentCompany,
    currentCompanyOperators: state.currentCompanyOperators,
  };
};

@connect(mapStateToProps)
@withRouter
export class ScheduleSettings extends Component {

  state = {
    isLoading: false,
    monthSelectItems: [],
    schedule: [],
    selectedOperatorId: null,
    selectedMonthName: null,
    isEditPopupVisible: false,
    isOperatorListExpandedOnMobile: false,
  };

  MONTH_ITEMS_LENGTH = 3;

  componentDidMount(){
    this.setState({
      monthSelectItems: this.generateMonthSelectItems(),
    });
    exposeComponentToGlobalScope(this);
  }

  componentDidUpdate(){
    const { currentCompanyOperators } = this.props;
    if (currentCompanyOperators.length) {
      this.onUrlParamsChange(updatedUrlParams => {
        if (updatedUrlParams.selectedOperatorId || updatedUrlParams.selectedMonthName) {
          const { selectedOperatorId, selectedMonthName } = this.state;
          this.fetchAndSetCalendar(
            updatedUrlParams.selectedOperatorId || selectedOperatorId,
            updatedUrlParams.selectedMonthName || selectedMonthName,
          );
        }
      });
    }
  }

  onUrlParamsChange = (callback) => {
    const { match } = this.props;
    const { operatorId, monthName, isEditing } = match.params;
    const { monthSelectItems, selectedOperatorId, selectedMonthName, isEditPopupVisible } = this.state;

    let redirectOperatorId;
    let redirectMonthName;
    let redirectIsEditing;

    const selectedOperator = this.getOperatorById(operatorId);
    if (!selectedOperator.id && operatorId !== 'all') {
      redirectOperatorId = 'all';
    }
    let isValidMonth = this.isValidMonth(monthName);
    if (!isValidMonth) {
      isValidMonth = this.isValidMonth(selectedMonthName);
      redirectMonthName = isValidMonth ? selectedMonthName : monthSelectItems[0].value;
    }
    if (isEditing) {
      if ((redirectOperatorId || operatorId) === 'all') {
        redirectIsEditing = '';
      }
      else if (isEditing !== 'edit') {
        redirectIsEditing = 'edit';
      }
    }
    const updatedState = {
      selectedOperatorId: redirectOperatorId || operatorId,
      selectedMonthName: redirectMonthName || monthName,
      isEditPopupVisible: !isNil(redirectIsEditing) ? Boolean(redirectIsEditing) : Boolean(isEditing),
    };
    if (redirectOperatorId || redirectMonthName || !isNil(redirectIsEditing)) {
      const redirectUrl = this.generateScheduleUrl(
        updatedState.selectedOperatorId,
        updatedState.selectedMonthName,
        updatedState.isEditPopupVisible,
      );
      browserHistory.push(redirectUrl);
    }
    const currentState = { selectedOperatorId, selectedMonthName, isEditPopupVisible };
    if (!isEqual(updatedState, { selectedOperatorId, selectedMonthName, isEditPopupVisible })) {
      this.setState(updatedState);
      const stateDiff = objectDiffShallow(currentState, updatedState);
      callback(stateDiff);
    }
  };

  fetchAndSetCalendar = (operatorId, monthName) => {
    this.setState({ isLoading: true });
    const { currentCompany } = this.props;
    const { startDate, endDate } = this.convertMonthNameIntoDateRange(monthName);
    const apiMethod = operatorId === 'all' ? getCalendarHeatmap : getCalendarByOperatorId;
    const apiParams = {
      companyId: currentCompany.id,
      operatorId,
      startDate,
      endDate,
    };

    apiMethod(apiParams)
      .then(schedule => {
        this.setState({
          schedule: schedule.sort((a, b) => {
            return +new Date(a.beginTimestamp) < +new Date(b.beginTimestamp) ? -1 : 1;
          }),
        });
      })
      .catch(() => {
        this.setState({ schedule: [] });
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  isValidMonth = (monthName) => {
    const { monthSelectItems } = this.state;
    return Boolean(
      find(monthSelectItems, month => month.value === monthName)
    );
  };

  convertMonthNameIntoDateRange = (monthName = '') => {
    return withMomentLocale('en', () => {
      const monthIndex = moment(monthName, 'MMMM').get('month');
      if (monthIndex === -1) {
        return null;
      }

      const currentMonth = moment().month();
      const yearOffset = currentMonth > monthIndex ? 1 : 0;
      const year = moment().year() + yearOffset;

      const startDate = moment.utc({
        y: year,
        M: monthIndex,
        d: 1,
        h: 0,
        m: 0,
        s: 0
      });

      const endDate = startDate.clone().add(1, 'month').subtract(1, 'days');

      return {
        startDate: startDate.format('Y-MM-DD'),
        endDate: endDate.format('Y-MM-DD'),
      };
    });
  };

  getMonthNameByIndex = (monthIndex, locale) => {
    return withMomentLocale(locale, () => {
      return moment().month(monthIndex).format('MMMM');
    });
  };

  generateMonthSelectItems = () => {
    const currentMonthIndex = new Date().getMonth();
    const monthIndexes = range(currentMonthIndex, currentMonthIndex + this.MONTH_ITEMS_LENGTH);
    return monthIndexes.map(monthIndex => {
      return {
        title: upperFirst(this.getMonthNameByIndex(monthIndex, 'ru')),
        value: this.getMonthNameByIndex(monthIndex, 'en').toLowerCase(),
      };
    });
  };

  onMonthSelectChange = (data) => {
    const { selectedOperatorId } = this.state;
    const monthName = data.value;
    browserHistory.push(
      this.generateScheduleUrl(selectedOperatorId, monthName, false)
    )
  };

  onEditPopupClose = () => {
    const { selectedOperatorId, selectedMonthName } = this.state;
    browserHistory.push(
      this.generateScheduleUrl(selectedOperatorId, selectedMonthName, false)
    );
  };

  generateScheduleUrl = (operatorId, monthName, isEditing) => {
    let url = `/schedule/${operatorId}`;
    if (monthName) {
      url += `/${monthName}`;
      if (isEditing) {
        url += '/edit';
      }
    }
    return url;
  };

  getOperatorById = (operatorId) => {
    const { currentCompanyOperators } = this.props;
    return find(currentCompanyOperators, { id: operatorId }) || {};
  };

  toggleOperatorListOnMobile = (isOperatorListExpandedOnMobile) => {
    this.setState({ isOperatorListExpandedOnMobile })
  };

  render(){
    const {
      monthSelectItems,
      selectedOperatorId,
      selectedMonthName,
      isEditPopupVisible,
      schedule,
      isLoading,
      isOperatorListExpandedOnMobile,
    } = this.state;

    const selectedOperator = this.getOperatorById(selectedOperatorId);

    return (
      <div className={cn({
        [styles.container]: true,
        [styles.containerOverlap]: isEditPopupVisible,
        [styles.containerOperatorListExpanded]: isOperatorListExpandedOnMobile,
      })}>
        <ScheduleSettingsOperatorList className={styles.operatorList}
          onSelect={() => this.toggleOperatorListOnMobile(false)}/>

        <div className={styles.calendarContainer}>
          <div className={styles.calendarActions}>

            {isOperatorListExpandedOnMobile && (
              <div className={styles.calendarMobileMenuBackground}
                onClick={() => this.toggleOperatorListOnMobile(false)}/>
            )}

            <i className={cn('icon-users', styles.calendarMobileMenuIcon)}
              onClick={() => this.toggleOperatorListOnMobile(true)}/>

            <EditableSelect
              value={selectedMonthName}
              options={monthSelectItems}
              onChange={this.onMonthSelectChange}
              overrideClassNames={{
                container: styles.calendarMonthSelect,
                list: styles.calendarMonthSelectList,
                value: styles.calendarMonthSelectValue,
                valueArrow: styles.calendarMonthSelectValueArrow,
              }}/>

            {selectedOperatorId !== 'all' && (
              <Link className={styles.calendarEditButton}
                to={this.generateScheduleUrl(selectedOperatorId, selectedMonthName, true)}>
                <i className={cn(styles.calendarEditButtonIcon, 'icon-gear')}/>
                Настроить {!isMobileSizeScreen() && 'график работы'}
              </Link>
            )}
          </div>

          <ScheduleSettingsCalendar
            className={styles.calendar}
            month={selectedMonthName}
            schedule={schedule}
            isLoading={isLoading}
            isHeatmap={selectedOperatorId === 'all'}/>
        </div>

        {isEditPopupVisible && (
          <ScheduleSettingsEditPopup
            className={styles.editPopup}
            operator={selectedOperator}
            onChange={() => this.fetchAndSetCalendar(selectedOperatorId, selectedMonthName)}
            onClose={this.onEditPopupClose}/>
        )}
      </div>
    );
  }
}
